import React, { useEffect, useMemo, useState } from 'react';
import { Autocomplete, CircularProgress, TextField, TextFieldProps } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { getAuthToken } from '../../lib/authToken';

export interface ClientAutocompleteOption {
  id: string;
  firstName?: string;
  lastName?: string;
  email?: string;
  entityLegalName?: string;
  [key: string]: unknown;
}

interface ClientAutocompleteProps {
  value: ClientAutocompleteOption | string | null;
  onChange: (client: ClientAutocompleteOption | null) => void;
  label?: string;
  placeholder?: string;
  disabled?: boolean;
  required?: boolean;
  fullWidth?: boolean;
  size?: 'small' | 'medium';
  textFieldProps?: Partial<TextFieldProps>;
  extraParams?: Record<string, string>;
}

const authHeaders = () => ({
  Authorization: `Bearer ${getAuthToken()}`,
});

const labelFor = (option: ClientAutocompleteOption) => {
  const name = [option.firstName, option.lastName].filter(Boolean).join(' ').trim();
  const entity = option.entityLegalName || '';
  if (name && entity) return `${name} (${entity})`;
  return name || entity || option.email || option.id;
};

/**
 * Server-search client picker (Wave P.1). Debounced `?search=` against GET /api/clients.
 */
const ClientAutocomplete: React.FC<ClientAutocompleteProps> = ({
  value,
  onChange,
  label = 'Client',
  placeholder = 'Search clients…',
  disabled = false,
  required = false,
  fullWidth = true,
  size = 'medium',
  textFieldProps,
  extraParams,
}) => {
  const [inputValue, setInputValue] = useState('');
  const [debounced, setDebounced] = useState('');

  useEffect(() => {
    const t = setTimeout(() => setDebounced(inputValue.trim()), 250);
    return () => clearTimeout(t);
  }, [inputValue]);

  const selectedId = typeof value === 'string' ? value : value?.id || null;

  const { data: selectedClient } = useQuery({
    queryKey: ['client-autocomplete-selected', selectedId],
    enabled: Boolean(selectedId) && (typeof value === 'string' || !value?.firstName),
    queryFn: async (): Promise<ClientAutocompleteOption | null> => {
      const res = await fetch(`/api/clients/${selectedId}`, { headers: authHeaders() });
      if (!res.ok) return null;
      const json = await res.json();
      return (json.data?.client || json.data) as ClientAutocompleteOption;
    },
    staleTime: 60_000,
  });

  const { data: options = [], isFetching } = useQuery({
    queryKey: ['client-autocomplete', debounced, extraParams],
    queryFn: async (): Promise<ClientAutocompleteOption[]> => {
      const params = new URLSearchParams({ limit: '20', ...(extraParams || {}) });
      if (debounced) params.set('search', debounced);
      const res = await fetch(`/api/clients?${params.toString()}`, { headers: authHeaders() });
      if (!res.ok) return [];
      const json = await res.json();
      return (json.data?.clients || []) as ClientAutocompleteOption[];
    },
    staleTime: 15_000,
  });

  const resolvedValue = useMemo(() => {
    if (!selectedId) return null;
    if (typeof value === 'object' && value?.id) return value;
    const fromOptions = options.find((o) => o.id === selectedId);
    if (fromOptions) return fromOptions;
    if (selectedClient?.id === selectedId) return selectedClient;
    return { id: selectedId, firstName: 'Loading…' } as ClientAutocompleteOption;
  }, [selectedId, value, options, selectedClient]);

  const mergedOptions = useMemo(() => {
    if (!resolvedValue?.id) return options;
    if (options.some((o) => o.id === resolvedValue.id)) return options;
    return [resolvedValue, ...options];
  }, [options, resolvedValue]);

  return (
    <Autocomplete
      options={mergedOptions}
      value={resolvedValue}
      onChange={(_, next) => onChange(next)}
      inputValue={inputValue}
      onInputChange={(_, next, reason) => {
        if (reason === 'reset' && resolvedValue) {
          setInputValue(labelFor(resolvedValue));
          return;
        }
        setInputValue(next);
      }}
      getOptionLabel={(option) => labelFor(option)}
      isOptionEqualToValue={(a, b) => a.id === b.id}
      filterOptions={(x) => x}
      loading={isFetching}
      disabled={disabled}
      fullWidth={fullWidth}
      size={size}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          placeholder={placeholder}
          required={required}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {isFetching ? <CircularProgress color="inherit" size={18} /> : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
          {...textFieldProps}
        />
      )}
    />
  );
};

export default ClientAutocomplete;
